import { useCallback, useState } from 'react'
import ApiEndpoints from '../commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 20000, maximumAge: 5000 }

function geoErrorMessage(err) {
  if (!err) return 'Could not read GPS'
  if (err.code === 1) return 'Location permission denied. Allow location in the browser to share your position.'
  if (err.code === 2) return 'Location unavailable.'
  if (err.code === 3) return 'Location request timed out.'
  return err.message || 'Could not read GPS'
}

function clampLat(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return 0
  return Math.max(-90, Math.min(90, x))
}

function clampLong(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return 0
  return Math.max(-180, Math.min(180, x))
}

export function readGeolocationOnce() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation is not supported in this browser'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: clampLat(pos.coords.latitude),
          long: clampLong(pos.coords.longitude),
        }),
      (err) => reject(err),
      GEO_OPTIONS,
    )
  })
}

/** Great-circle distance in kilometres between two WGS84 points. */
export function haversineDistanceKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return 6371 * c
}

export function formatDistanceKm(km) {
  if (!Number.isFinite(km)) return '—'
  if (km < 1) return `${Math.round(km * 1000)} m`
  if (km < 10) return `${km.toFixed(1)} km`
  return `${Math.round(km)} km`
}

export function sameCoords(a, b) {
  if (a == null || b == null) return false
  return (
    Number(a.lat).toFixed(6) === Number(b.lat).toFixed(6) &&
    Number(a.long).toFixed(6) === Number(b.long).toFixed(6)
  )
}

/** Speed in km/h from two points and elapsed wall time between fetches. */
export function computeSpeedKmh(fromLat, fromLon, toLat, toLon, elapsedMs) {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return null
  const km = haversineDistanceKm(fromLat, fromLon, toLat, toLon)
  const hours = elapsedMs / (1000 * 3600)
  if (hours <= 0) return null
  return km / hours
}

export function formatSpeedKmh(speed) {
  if (!Number.isFinite(speed)) return '—'
  if (speed < 1) return `${Math.round(speed * 1000)} m/h`
  return `${speed < 10 ? speed.toFixed(1) : Math.round(speed)} km/h`
}

/** ETA in seconds from straight-line distance and current speed. */
export function computeEtaSeconds(distanceKm, speedKmh) {
  if (!Number.isFinite(distanceKm) || !Number.isFinite(speedKmh) || speedKmh <= 0) return null
  return (distanceKm / speedKmh) * 3600
}

export function formatEtaSeconds(totalSec) {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return 'Arriving now'
  const sec = Math.round(totalSec)
  if (sec < 60) return `${sec} sec`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} min`
  const hr = Math.floor(min / 60)
  const remMin = min % 60
  if (remMin === 0) return `${hr} hr`
  return `${hr} hr ${remMin} min`
}

function parseApiError(error) {
  const status = error?.response?.status
  const data = error?.response?.data
  const msg =
    data?.message ||
    (Array.isArray(data?.errors) ? data.errors.join(' ') : null) ||
    error?.message ||
    'Request failed'
  return { status, msg }
}

function parseTimestamp(isoOrSql) {
  if (isoOrSql == null || isoOrSql === '') return null
  const s = String(isoOrSql).trim()
  const normalized = /^\d{4}-\d{2}-\d{2} \d/.test(s) ? s.replace(' ', 'T') : s
  const ms = Date.parse(normalized)
  return Number.isNaN(ms) ? null : ms
}

/**
 * Latest-location is fetched only when the UI calls {@link fetchLatest} (e.g. modal open).
 * Journey start/complete still call {@link fetchLatest} for that bus after success.
 */
export function useBusJourneyTracking() {
  const [latestByBusId, setLatestByBusId] = useState({})
  const [inFlight, setInFlight] = useState({})

  const setBusFlight = useCallback((busId, key) => {
    const id = Number(busId)
    setInFlight((prev) => {
      const next = { ...prev }
      if (key == null) delete next[id]
      else next[id] = key
      return next
    })
  }, [])

  const fetchLatest = useCallback(async (busId) => {
    const id = Number(busId)
    if (!id) return null
    try {
      const res = await ApiEndpoints.getTransportLatestLocation(id)
      const payload = res.data ?? {}
      const data = payload.data
      const backendMessage =
        typeof payload.message === 'string' ? payload.message.trim() : ''

      const hasCoords =
        data &&
        data.lat != null &&
        data.long != null &&
        Number.isFinite(Number(data.lat)) &&
        Number.isFinite(Number(data.long))

      const coords = hasCoords
        ? { lat: Number(data.lat), long: Number(data.long) }
        : null
      const updatedMs =
        parseTimestamp(data?.updated_at) ??
        parseTimestamp(data?.updatedAt) ??
        null

      /** e.g. { status: true, message: "No location found for this bus", data: null } */
      const noLocationMessage =
        !hasCoords && backendMessage ? backendMessage : null

      setLatestByBusId((prev) => ({
        ...prev,
        [id]: {
          coords,
          updatedMs,
          raw: data,
          noLocationMessage,
          error: null,
          lastFetchedMs: Date.now(),
        },
      }))
      return data
    } catch (error) {
      const { status, msg } = parseApiError(error)
      setLatestByBusId((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          error: status === 401 ? 'Not authorized to view this location.' : msg,
          lastFetchedMs: Date.now(),
        },
      }))
      return null
    }
  }, [])

  const startOrUpdateLocation = useCallback(
    async (busId, options = {}) => {
      const silent = Boolean(options.silent)
      const id = Number(busId)
      let position
      try {
        position = await readGeolocationOnce()
      } catch (e) {
        if (!silent) toast.error(geoErrorMessage(e))
        throw e
      }
      setBusFlight(id, 'start')
      try {
        const res = await ApiEndpoints.transportJourneyStart({
          bus_id: id,
          lat: position.lat,
          long: position.long,
        })
        const backendMsg =
          typeof res.data?.message === 'string' ? res.data.message.trim() : ''
        const fallback =
          res.status === 201
            ? 'Journey start location stored successfully'
            : 'Location updated'
        if (!silent) toast.success(backendMsg || fallback)
        await fetchLatest(id)
        return { ...res.data, _httpStatus: res.status }
      } catch (error) {
        if (!silent) {
          const { status, msg } = parseApiError(error)
          if (status === 409) toast.error(msg)
          else if (status === 403) toast.error(msg)
          else if (status === 401) toast.error(msg || 'Not authorized')
          else if (status === 422) toast.error(msg)
          else if (status === 404) toast.error(msg || 'Bus not found')
          else toast.error(msg)
        }
        throw error
      } finally {
        setBusFlight(id, null)
      }
    },
    [fetchLatest, setBusFlight],
  )

  const completeJourney = useCallback(
    async (busId) => {
      const id = Number(busId)
      setBusFlight(id, 'complete')
      try {
        const res = await ApiEndpoints.transportJourneyComplete({ bus_id: id })
        toast.success(res.data?.message || 'Journey completed')
        await fetchLatest(id)
        return res.data
      } catch (error) {
        const { status, msg } = parseApiError(error)
        if (status === 422) toast.error(msg)
        else if (status === 403) toast.error(msg)
        else if (status === 401) toast.error(msg || 'Not authorized')
        else toast.error(msg)
        throw error
      } finally {
        setBusFlight(id, null)
      }
    },
    [fetchLatest, setBusFlight],
  )

  return {
    latestByBusId,
    fetchLatest,
    startOrUpdateLocation,
    completeJourney,
    inFlight,
  }
}
